export function formatIsoDate(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function extractLocalDateFromIso(isoString?: string): string {
  if (!isoString) return formatIsoDate(new Date());
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(isoString) && !/Z|[+-]\d{2}/.test(isoString)) {
    return isoString.split('T')[0];
  }
  try {
    const d = new Date(isoString);
    if (!isNaN(d.getTime())) {
      return formatIsoDate(d);
    }
  } catch {}
  return formatIsoDate(new Date());
}

export function extractLocalTimeFromIso(isoString?: string): string {
  if (!isoString) {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(isoString) && !/Z|[+-]\d{2}/.test(isoString)) {
    const t = isoString.split('T')[1];
    return t.slice(0, 5);
  }
  try {
    const d = new Date(isoString);
    if (!isNaN(d.getTime())) {
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    }
  } catch {}
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

export function createIsoFromLocalDateAndTime(dateStr: string, timeStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm, 0).toISOString();
}

export function formatTime(isoString: string): string {
  if (!isoString) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(isoString) && !/Z|[+-]\d{2}/.test(isoString)) {
    const t = isoString.split('T')[1].slice(0, 5);
    const [h, m] = t.split(':').map(Number);
    const dummy = new Date();
    dummy.setHours(h, m, 0, 0);
    return dummy.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export function formatShortDate(isoString: string): string {
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export function formatFullDate(isoString: string): string {
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export function calculateLagHours(earlierIso: string, laterIso: string): number {
  const t1 = new Date(earlierIso).getTime();
  const t2 = new Date(laterIso).getTime();
  const diffHours = (t2 - t1) / (1000 * 60 * 60);
  return Math.round(diffHours * 10) / 10;
}

export function isWithinLagWindow(mealIso: string, symptomIso: string, minHours = 2, maxHours = 18): boolean {
  const lag = calculateLagHours(mealIso, symptomIso);
  return lag >= minHours && lag <= maxHours;
}

export function isNextMorningWindow(mealIso: string, symptomIso: string): boolean {
  const lag = calculateLagHours(mealIso, symptomIso);
  return lag >= 8 && lag <= 16;
}

export function toLocalDatetimeInputValue(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const hh = pad(date.getHours());
  const mm = pad(date.getMinutes());
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

export function getWeekDateRange(arg: string | number = 7): string {
  if (typeof arg === 'number') {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - arg);
    return `${formatShortDate(start.toISOString())} – ${formatShortDate(end.toISOString())}`;
  }
  const d = new Date(arg);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  const sunday = new Date(d.setDate(diff + 6));
  return `${formatShortDate(monday.toISOString())} – ${formatShortDate(sunday.toISOString())}`;
}

export function getDefaultMealType(timeArg?: string | Date): 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack' {
  let h = new Date().getHours();
  if (timeArg instanceof Date) {
    h = timeArg.getHours();
  } else if (typeof timeArg === 'string') {
    if (timeArg.includes('T')) {
      const parsed = new Date(timeArg);
      if (!isNaN(parsed.getTime())) {
        h = parsed.getHours();
      }
    } else if (timeArg.includes(':')) {
      const parsedH = parseInt(timeArg.split(':')[0], 10);
      if (!isNaN(parsedH)) {
        h = parsedH;
      }
    }
  }

  if (h >= 5 && h < 11) return 'Breakfast';
  if (h >= 11 && h < 16) return 'Lunch';
  if (h >= 16 && h < 22) return 'Dinner';
  return 'Snack';
}
